import { useState, useEffect, useMemo } from "react";
import type { Post, User, Comment } from '../types';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Loader2, ThumbsUp, Trash2, MessageCircle, Heart } from 'lucide-react';
import { format } from 'date-fns';
import { hr } from 'date-fns/locale';
import { useAuth } from '../hooks/use-auth';
import { toggleLikePost, deletePost, getComments, createComment } from '../lib/data';
import { cn } from '../lib/utils';
import { useToast } from "../hooks/use-toast";
import { useDoubleTap } from "../hooks/use-double-tap";
import { CommentInput } from './comment-input';
import { CommentList } from './comment-list';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';
import Linkify from './linkify';
import { VideoPlayer, VideoProcessingPlaceholder } from './video-player';
import { isVideoStillProcessing } from '../lib/video-upload';
import { CachedImage } from './cached-image';
import { StillSending } from './still-sending';
import { useOutbox } from '../hooks/use-outbox';

interface PostCardProps {
  post: Post & { author: User };
  onPostDeleted: (postId: string) => void;
}



export default function PostCard({ post, onPostDeleted }: PostCardProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const userInitials = post.author.name.split(' ').map(n => n[0]).join('');

  const [isLiked, setIsLiked] = useState(user ? (post.likedBy || []).includes(user.id) : false);
  const [likeCount, setLikeCount] = useState(post.likes);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCommentsMobile, setShowCommentsMobile] = useState(false);
  const [showCommentsDesktop, setShowCommentsDesktop] = useState(false);
  const [comments, setComments] = useState<(Comment & { author: User })[]>([]);
  const [showHeartAnimation, setShowHeartAnimation] = useState(false);
  // Only subscribe to comments when user opens the section — avoids N simultaneous listeners on feed load
  const [commentsSubscribed, setCommentsSubscribed] = useState(false);

  // This person's comments that are still on their way (e.g. written
  // without internet, then the app was closed): shown until they arrive.
  const outbox = useOutbox();
  const shownComments = useMemo(() => {
    if (!user) return comments;
    const prefix = `posts/${post.id}/comments/`;
    const ids = new Set(comments.map((comment) => comment.id));
    const waiting = outbox
      .filter((entry) => entry.uid === user.id && entry.path.startsWith(prefix))
      .map((entry) => ({ id: entry.path.slice(prefix.length), ...entry.data, author: user }) as Comment & { author: User })
      .filter((comment) => !ids.has(comment.id));
    if (!waiting.length) return comments;
    return [...comments, ...waiting].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }, [comments, outbox, post.id, user]);

  // Use denormalized count by default, real-time length once subscribed
  const currentCommentCount = commentsSubscribed ? shownComments.length : (post.commentCount || 0);

  const handleDoubleTapLike = () => {
    if (!user || isLiked) return; // Only like if not already liked
    handleLike();
    // Show heart animation
    setShowHeartAnimation(true);
    setTimeout(() => setShowHeartAnimation(false), 1000);
  };

  const handleDoubleTap = useDoubleTap({
    onDoubleTap: handleDoubleTapLike,
  });

  // Only subscribe to comments when opened — avoids N simultaneous listeners on scroll
  useEffect(() => {
    if (!commentsSubscribed || !post.id) return;
    const unsubscribe = getComments(post.id, (newComments) => {
      setComments(newComments);
    });
    return () => unsubscribe();
  }, [commentsSubscribed, post.id]);

  const handleCreateComment = async (content: string, mediaUrl?: string, mediaType?: "image" | "video" | "gif" | "sticker") => {
    if (!user) return;
    await createComment(post.id, user.id, content, mediaUrl, mediaType);
  }

  // Effect to sync like state when post data changes from parent
  useEffect(() => {
    if (user) {
      setIsLiked((post.likedBy || []).includes(user.id));
    }
    setLikeCount(post.likes);
  }, [post.likes, post.likedBy, user]);


  const handleLike = async () => {
    if (!user) return;

    // Optimistic update
    const previousIsLiked = isLiked;
    const previousLikeCount = likeCount;

    setIsLiked(!previousIsLiked);
    setLikeCount(previousIsLiked ? previousLikeCount - 1 : previousLikeCount + 1);

    try {
      await toggleLikePost(post.id, user.id);
    } catch (error) {
      // Revert on error
      setIsLiked(previousIsLiked);
      setLikeCount(previousLikeCount);
      console.error("Failed to toggle like:", error);
      toast({
        file: "components/post-card.tsx", code: "NN-ERR-034",
        variant: 'destructive', title: 'Greška', description: 'Nije moguće ažurirati lajk.'
      });
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    const canDelete = user.role === 'admin' || user.role === 'trainer' || user.role === 'glavni_trener';
    if (!canDelete) return;

    setIsDeleting(true);
    try {
      await deletePost(post.id);
      toast({
        file: "components/post-card.tsx", code: "NN-SUC-035",
        title: 'Objava obrisana', description: 'Objava je uklonjena.'
      });
      onPostDeleted(post.id);
    } catch (error) {
      console.error("Failed to delete post:", error);
      toast({
        file: "components/post-card.tsx", code: "NN-ERR-034",
        variant: 'destructive', title: 'Greška', description: 'Nije moguće obrisati objavu.'
      });
      setIsDeleting(false);
    }
  };

  const canDelete = user && ['admin', 'trainer', 'glavni_trener'].includes(user.role);

  let mediaUrls = post.mediaType === 'image' ? post.imageUrls : post.videoUrls;

  // For backward compatibility
  const hasMedia = mediaUrls && mediaUrls.length > 0;
  if (!hasMedia && (post.imageUrl || post.videoUrl)) {
    if (post.imageUrl) {
      post.mediaType = 'image';
      mediaUrls = [post.imageUrl];
    } else if (post.videoUrl) {
      post.mediaType = 'video';
      mediaUrls = [post.videoUrl];
    }
  }


  return (
    <Card className="overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
      <CardHeader className="flex flex-row items-center gap-4">
        <Avatar>
          <AvatarImage src={post.author.avatar} alt={post.author.name} />
          <AvatarFallback>{userInitials}</AvatarFallback>
        </Avatar>
        <div className="grid gap-1 flex-1">
          <CardTitle className="text-base">{post.author.name}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {format(new Date(post.createdAt), "d. MMMM yyyy. 'u' p", { locale: hr })}
          </p>
          <StillSending path={`posts/${post.id}`} />
        </div>
        {canDelete && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" disabled={isDeleting} className="hover:bg-transparent">
                {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 text-destructive hover:text-[#e73e7b] transition-colors" />}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Jeste li apsolutno sigurni?</AlertDialogTitle>
                <AlertDialogDescription>
                  Ova se radnja ne može poništiti. Ovo će trajno obrisati objavu.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Otkaži</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive hover:bg-[#e73e7b]"
                  onClick={handleDelete}>
                  Obriši
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </CardHeader>
      <CardContent>
        {post.content && <p className="text-base whitespace-pre-wrap mb-4"><Linkify>{post.content}</Linkify></p>}

        {mediaUrls && mediaUrls.length > 0 && (
          <div className="relative">
            <Carousel className="w-full rounded-lg overflow-hidden border bg-black -mx-1">
              <CarouselContent>
                {mediaUrls.map((url, index) => (
                  <CarouselItem key={index}>
                    <div
                      className="flex justify-center items-center bg-black cursor-pointer select-none"
                      onClick={handleDoubleTap}
                    >
                      {post.mediaType === 'image' ? (
                        <CachedImage
                          src={url}
                          alt={`Slika objave ${index + 1}`}
                          className="max-h-[80vh] w-auto h-auto object-contain pointer-events-none"
                          data-ai-hint="fitness lifestyle"
                        />
                      ) : (
                        isVideoStillProcessing(post) ? (
                          <VideoProcessingPlaceholder />
                        ) : (
                          <VideoPlayer
                            url={url}
                            poster={post.videoThumbnailUrl}
                            className="max-h-[80vh] w-full h-auto"
                          />
                        )
                      )}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {mediaUrls.length > 1 && (
                <>
                  <CarouselPrevious className="absolute left-2" />
                  <CarouselNext className="absolute right-2" />
                </>
              )}
            </Carousel>
            {/* Heart animation overlay */}
            {showHeartAnimation && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <Heart className="h-24 w-24 text-red-500 fill-current animate-ping" />
              </div>
            )}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-col items-stretch border-t border-border pt-4 gap-4">
        <div className="flex gap-4">
          <Button variant="ghost" size="sm" className="group flex items-center gap-2 text-muted-foreground hover:text-[#e73e7b] hover:bg-transparent" onClick={handleLike} disabled={!user}>
            <ThumbsUp className={cn("h-4 w-4 transition-colors", isLiked && "fill-current text-primary", "group-hover:text-[#e73e7b]")} />
            <span className="transition-colors">{likeCount}</span>
          </Button>

          {/* Mobile - Inline expansion (working version) */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              className="group flex items-center gap-2 text-muted-foreground hover:text-[#e73e7b] hover:bg-transparent"
              onClick={() => { setShowCommentsMobile(!showCommentsMobile); setCommentsSubscribed(true); }}
            >
              <MessageCircle className="h-4 w-4 transition-colors group-hover:text-[#e73e7b]" />
              {currentCommentCount > 0 && (
                <span className="transition-colors">{currentCommentCount}</span>
              )}
            </Button>
          </div>

          {/* Desktop Inline */}
          <div className="hidden md:block">
            <Button variant="ghost" size="sm" className="group flex items-center gap-2 text-muted-foreground hover:text-[#e73e7b] hover:bg-transparent" onClick={() => { setShowCommentsDesktop(!showCommentsDesktop); setCommentsSubscribed(true); }}>
              <MessageCircle className="h-4 w-4 transition-colors group-hover:text-[#e73e7b]" />
              {currentCommentCount > 0 && (
                <span className="transition-colors">{currentCommentCount}</span>
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Comments Section - Inline with max height */}
        {showCommentsMobile && (
          <div className="md:hidden w-full pt-2 border-t mt-2">
            <div className="max-h-[60vh] overflow-y-auto">
              <CommentList postId={post.id} comments={shownComments} />
            </div>
            <CommentInput postId={post.id} onComment={handleCreateComment} isMobile={true} />
          </div>
        )}

        {/* Desktop Comments Section */}
        {showCommentsDesktop && (
          <div className="hidden md:block w-full pt-2">
            <CommentList postId={post.id} comments={shownComments} />
            <CommentInput postId={post.id} onComment={handleCreateComment} isMobile={false} />
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
